/* eslint-disable @dhl/generic/no-hardcoded-translations */
import type { FocusEvent, KeyboardEvent, MouseEvent, PropsWithChildren } from 'react'
import { BareButton } from '@dhl-ui/components'
import { type PageMenuItem } from './types'

type Props = PropsWithChildren<
  Pick<PageMenuItem, 'link' | 'isActive' | 'isDisabled'> & {
    /**
     * Unique identifier
     */
    id: string
    /**
     * Flag whether it is for a submenu item or not
     * @default false
     */
    isSubMenuItem?: boolean
    /**
     * When our user selects a menu item
     */
    onClick: (event: MouseEvent<HTMLElement>) => void

    onFocus?: (event: FocusEvent<HTMLElement>) => void

    onMouseEnter?: (event: MouseEvent<HTMLElement>) => void

    onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void

    isExpanded?: boolean
    isCollapsedMenuOpenForItem?: boolean
  }
>

export const SideMenuItem = ({
  link,
  isActive,
  isDisabled,
  id,
  onClick,
  onFocus,
  onMouseEnter,
  onKeyDown,
  children,
  isSubMenuItem = false,
  isExpanded,
  isCollapsedMenuOpenForItem,
  ...rest
}: Props) => {
  const black = 'grey.700'
  const testId = `page-side-menu-${id}`
  const activeColor = 'components.navbar.contrast'
  const color = isActive ? activeColor : black

  return (
    <BareButton
      {...rest}
      component="a"
      id={id}
      href={link}
      onClick={onClick}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onKeyDown={onKeyDown}
      data-testid={testId}
      disabled={isDisabled}
      sx={({ spacing, misc }) => ({
        color,
        mb: isExpanded ? 0.25 : 0.5,
        borderRadius: misc.borderRadius(2),
        bgcolor: isActive ? 'components.navbar.selected' : undefined,
        textWrap: 'nowrap',
        overflow: 'hidden',
        position: 'relative',
        fontWeight: isActive ? 'bold' : undefined,

        ...(isSubMenuItem
          ? {
              p: spacing(1, 2, 1, 4.5),
              boxShadow: isActive ? `inset 2px 0 0 0 ${activeColor}` : 'none',
              minHeight: spacing(6),
              display: 'flex',
            }
          : {
              p: isExpanded ? spacing(1, 2) : spacing(1.5, 1),
              minHeight: spacing(7),
              justifyContent: 'flex-start',
            }),

        '&:hover': {
          bgcolor: isActive ? 'components.navbar.selectedHover' : 'action.hover',
          opacity: 1,
          borderColor: isActive ? activeColor : 'grey.300',
        },

        ...misc.createFocusVisibleState({
          ...(isSubMenuItem ? {} : { bgcolor: isActive ? 'components.navbar.selected' : undefined }),
          outline: misc.borderStyle('info.main', 3),
          outlineOffset: misc.borderSize(2),
        }),

        '&.Mui-disabled': {
          bgcolor: 'action.disabledBackground',
        },

        '&.Mui-focusVisible': {
          bgcolor: isActive ? 'components.navbar.selectedHover' : 'action.hover',
          opacity: 1,
          borderColor: isActive ? activeColor : 'grey.300',
        },
      })}
      aria-current={isActive ? 'page' : undefined}
      // only for main menu
      {...(isSubMenuItem
        ? {}
        : {
            'aria-haspopup': !isExpanded,
            'aria-controls': isCollapsedMenuOpenForItem ? 'submenu-menu' : undefined,
            'aria-expanded': isCollapsedMenuOpenForItem ? 'true' : undefined,
          })}
    >
      {children}
    </BareButton>
  )
}
